'use client';

/**
 * What the guest sees while the counter decides, and after.
 * docs/operational-ux-2026-09/07-customer-ordering.md §5
 *
 * Three states, and the middle one is the whole reason this flow exists:
 *
 *     SUBMITTED    "Sent to Paris Place — they'll confirm in a moment"
 *     CONFIRMED    "Confirmed. Pay within 4:47"        [ Pay ₵150 ]
 *     DECLINED     "Jollof Rice just sold out"          [ Order something else ]
 *
 * Copy rules this file holds to (doc 04 §5, doc 07 §5):
 *
 *   · No engine word ever reaches the guest. The wording comes from
 *     `orderStatusWord(status, 'guest', …)`, which is policy-aware: under
 *     approval, `placed` reads "Waiting for them to confirm" rather than
 *     "Sent", because telling someone their order was sent while the counter
 *     has not agreed to make it is the exact false promise this flow exists to
 *     prevent.
 *   · A decline NAMES its reason in plain words and offers the next action. A
 *     dead end is the failure state — someone who is told "no" with nowhere to
 *     go has been abandoned mid-transaction.
 *   · The countdown is human (`4:47`), and at zero the screen becomes an offer
 *     to start again with the cart intact. Losing the cart on expiry would
 *     punish the guest twice for a slow connection.
 *
 * Deliberately self-contained and presentational: it takes an order-shaped
 * object and two callbacks. The surface that owns the order wires it up, which
 * keeps this out of the 3,000-line ordering component it renders inside.
 */

import { useEffect, useState } from 'react';

import {
  DECLINE_REASON_GUEST_COPY,
  isAwaitingMerchantApproval,
  isDeclineReason,
  paymentWindowSecondsRemaining,
  readPaymentDeadline,
} from '@/lib/pickup-approval';
import { orderStatusWord } from '@/lib/order-status-vocabulary';

export interface PickupApprovalOrder {
  status: string;
  metadata?: Record<string, unknown> | null;
  total?: number | null;
  currency?: string | null;
  payment_status?: string | null;
}

export function PickupApprovalStatus({
  order,
  branchName,
  etaLabel,
  formatTotal,
  onPay,
  onStartOver,
}: {
  order: PickupApprovalOrder;
  branchName?: string | null;
  /**
   * The promise the counter made when it accepted ("Ready in about 10–15 min."),
   * usually `eta.label` from the status API. Must be shown BEFORE the guest is
   * asked to pay: the range they are paying into is the whole reason to pay now
   * rather than walk away.
   */
  etaLabel?: string | null;
  /** Money formatting belongs to the caller — it owns the currency context. */
  formatTotal: (amount: number, currency: string) => string;
  onPay: () => void;
  /** Browse again, cart intact. */
  onStartOver: () => void;
}) {
  const secondsLeft = useCountdown(order);
  const awaiting = isAwaitingMerchantApproval(order.metadata);
  const declined = order.status === 'cancelled';
  const expired = order.status === 'expired';
  const confirmed = order.status === 'accepted' && !expired;

  // Policy-aware wording, so `placed` reads as "waiting for them to confirm"
  // rather than the plain "Sent" a non-approval order gets.
  const heading =
    orderStatusWord(order.status, 'guest', {
      pickupApproval: true,
      fulfilment: 'pickup',
      paySecondsRemaining: secondsLeft,
    }) ?? 'Your order';

  if (declined) {
    return (
      <Panel tone="ended" icon="✕" heading="They can’t make this right now">
        <p className="text-sm text-muted-foreground">{declineCopy(order.metadata)}</p>
        <Action onClick={onStartOver}>Order something else</Action>
      </Panel>
    );
  }

  if (expired) {
    return (
      <Panel tone="ended" icon="⏱" heading="This order expired">
        <p className="text-sm text-muted-foreground">
          Payment didn’t come through in time, so {branchName || 'they'} released it. Your
          basket is still here.
        </p>
        <Action onClick={onStartOver}>Start again</Action>
      </Panel>
    );
  }

  if (confirmed) {
    const outOfTime = secondsLeft !== null && secondsLeft <= 0;
    return (
      <Panel tone="live" icon="✓" heading={outOfTime ? 'Time’s up' : heading}>
        {outOfTime ? (
          <>
            <p className="text-sm text-muted-foreground">
              The payment window closed. Your basket is still here.
            </p>
            <Action onClick={onStartOver}>Start again</Action>
          </>
        ) : (
          <>
            {etaLabel ? (
              <p className="text-sm font-medium text-foreground">{etaLabel}</p>
            ) : null}
            {secondsLeft !== null ? (
              <p
                className="text-3xl font-bold tabular-nums"
                // Announced once a minute rather than every tick: a screen
                // reader reciting a countdown per second is unusable.
                aria-live="polite"
                aria-atomic="true"
              >
                {formatCountdown(secondsLeft)}
              </p>
            ) : null}
            <Action onClick={onPay}>
              Pay {formatTotal(Number(order.total ?? 0), order.currency || 'GHS')}
            </Action>
            {/* No payment window means the branch takes money at collection, so
                paying online now is optional. This is the first moment the guest
                is asked about payment at all — approval comes first. */}
            {secondsLeft === null && order.payment_status !== 'paid' ? (
              <p className="text-center text-xs text-muted-foreground">
                or pay when you collect it
              </p>
            ) : null}
          </>
        )}
      </Panel>
    );
  }

  // Awaiting the counter — or any other pre-decision state.
  return (
    <Panel tone="waiting" icon="⏳" heading={heading}>
      <p className="text-sm text-muted-foreground">
        {awaiting
          ? `${branchName || 'They'} will confirm in a moment. You’ll be asked to pay once they do.`
          : 'Hang tight.'}
      </p>
    </Panel>
  );
}

/**
 * Ticks once a second while a window is running, and stops dead when it is not.
 * An interval that keeps firing on a terminal state is a background wakeup on a
 * phone in someone's pocket.
 */
function useCountdown(order: PickupApprovalOrder): number | null {
  const deadline = readPaymentDeadline(order.metadata);
  const [seconds, setSeconds] = useState<number | null>(() =>
    paymentWindowSecondsRemaining(order.metadata),
  );

  useEffect(() => {
    if (!deadline) {
      setSeconds(null);
      return;
    }
    setSeconds(paymentWindowSecondsRemaining(order.metadata));
    const timer = setInterval(() => {
      const next = paymentWindowSecondsRemaining(order.metadata);
      setSeconds(next);
      if (next !== null && next <= 0) clearInterval(timer);
    }, 1000);
    return () => clearInterval(timer);
    // `deadline` is the only thing that changes the schedule; re-reading
    // `metadata` each tick keeps it correct without re-running the effect.
  }, [deadline?.getTime(), order.metadata]);

  return seconds;
}

function declineCopy(metadata: Record<string, unknown> | null | undefined): string {
  const reason = metadata?.decline_reason;
  const note = typeof metadata?.decline_note === 'string' ? metadata.decline_note.trim() : '';
  const base = isDeclineReason(reason)
    ? DECLINE_REASON_GUEST_COPY[reason]
    : 'They can’t take this order right now.';
  return note ? `${base} — ${note}` : base;
}

function formatCountdown(seconds: number): string {
  const safe = Math.max(0, seconds);
  return `${Math.floor(safe / 60)}:${String(safe % 60).padStart(2, '0')}`;
}

// ─── Presentation ────────────────────────────────────────────────────────────

function Panel({
  tone,
  icon,
  heading,
  children,
}: {
  tone: 'waiting' | 'live' | 'ended';
  icon: string;
  heading: string;
  children: React.ReactNode;
}) {
  const border =
    tone === 'live'
      ? 'border-[var(--color-success)]/40'
      : tone === 'ended'
        ? 'border-border'
        : 'border-[var(--color-warning)]/40';

  return (
    <section
      className={`space-y-3 rounded-2xl border ${border} bg-card p-5 text-center`}
      aria-label="Order status"
    >
      <p className="text-3xl" aria-hidden="true">
        {icon}
      </p>
      <h2 className="text-lg font-semibold">{heading}</h2>
      {children}
    </section>
  );
}

function Action({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      // 56px: this is the only thing on screen the guest is meant to do.
      className="min-h-[56px] w-full rounded-xl bg-foreground px-5 text-base font-semibold text-background [touch-action:manipulation] active:scale-[0.99]"
    >
      {children}
    </button>
  );
}
